// src/components/MusicToggle.js
// Floating background-music control, fixed to the top-right corner.
//
// Browsers block autoplay-WITH-SOUND until the user performs an ACTIVATION
// gesture (click / tap / keypress — scrolling does NOT count in Chrome). So if
// the user hasn't muted it, we start the track on the first such gesture
// anywhere on the page (also after a refresh). The on/off choice persists in
// localStorage.
import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const TRACK = '/media/alexzavesa-dance-playful-night-510786.mp3';
const DEFAULT_VOLUME = 0.5;
const PREF_KEY = 'zabra_music_on';

const wantsMusic = () => {
  try {
    return localStorage.getItem(PREF_KEY) !== 'off'; // default ON
  } catch {
    return true;
  }
};
const savePref = (on) => {
  try {
    localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    /* ignore */
  }
};

const MusicToggle = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const startedRef = useRef(false); // has the track ever successfully started?

  useEffect(() => {
    const audio = new Audio(TRACK);
    audio.loop = true;
    audio.volume = DEFAULT_VOLUME;
    audio.preload = 'auto';
    audioRef.current = audio;

    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    // First-gesture autostart. We ALWAYS listen (not gated on the stored pref),
    // and decide at gesture time whether to play. We only detach the listeners
    // once the track has actually begun — a blocked/failed play keeps us armed.
    const gestures = ['pointerdown', 'click', 'keydown', 'touchstart'];
    const onGesture = () => {
      const a = audioRef.current;
      if (!a) return;
      if (!wantsMusic()) return; // user muted → stay silent, but keep listening
      if (!a.paused) return;
      a.volume = DEFAULT_VOLUME;
      a.play()
        .then(() => {
          startedRef.current = true;
          detach(); // succeeded — no longer need the global listeners
        })
        .catch(() => {
          /* blocked; stay armed for the next gesture */
        });
    };
    const detach = () => gestures.forEach((g) => window.removeEventListener(g, onGesture));
    gestures.forEach((g) => window.addEventListener(g, onGesture, { passive: true }));

    return () => {
      detach();
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      savePref(true);
      audio.volume = DEFAULT_VOLUME;
      audio.play().catch(() => {});
    } else {
      audio.pause();
      savePref(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={playing ? 'Senyapkan muzik' : 'Mainkan muzik'}
      title={playing ? 'Senyapkan muzik' : 'Mainkan muzik'}
      className={`fixed top-4 right-4 md:top-6 md:right-6 z-[70] grid h-11 w-11 place-items-center rounded-full border-2 border-[var(--z-ink)] shadow-[0_4px_0_0_var(--z-ink)] active:translate-y-0.5 transition ${
        playing ? 'bg-[var(--z-accent)] text-white' : 'bg-white text-[var(--z-ink)]'
      }`}
    >
      {playing ? <Volume2 size={20} /> : <VolumeX size={20} />}
      {playing && (
        <span className="absolute inset-0 rounded-full border-2 border-[var(--z-accent)] animate-ping opacity-40" />
      )}
    </button>
  );
};

export default MusicToggle;
